import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Form, Badge, InputGroup, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    CheckCircle, AlertTriangle, Info, XCircle, Trash2, Eye, EyeOff,
    Calendar, Award, Bell, Clock, Search, Star, StarOff, BellIcon, ChevronLeft, Filter, AlertTriangle as AlertIcon
} from 'lucide-react';
import { Content } from '../../../_theme/layout/components/Content';
import { useNavigate } from 'react-router-dom';
import { NotificationDetailModal } from './acknowledgementpopup';
import { ApiResponse, INotificationItem } from '../core/_models';
import { updateNotificationFlags, deleteNotificationFlags, getNotifications } from '../core/_request';
import { CustomDateFormate } from '../../../utils/date';

// Constants (outside component - no need for memo)
const TYPE_CONFIG = {
    success: { icon: CheckCircle, gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#10b981', bg: '#f0fdf4', border: '#10b981' },
    warning: { icon: AlertTriangle, gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#f59e0b', bg: '#fffbeb', border: '#f59e0b' },
    danger: { icon: XCircle, gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#ef4444', bg: '#fef2f2', border: '#ef4444' },
    info: { icon: Info, gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#3b82f6', bg: '#eff6ff', border: '#3b82f6' },
};

const TYPE_FILTERS = [
    { id: 'all', label: 'All Types', icon: Bell, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { id: 'danger', label: 'Alert', icon: XCircle, gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
    { id: 'warning', label: 'Warning', icon: AlertTriangle, gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
    { id: 'success', label: 'Success', icon: CheckCircle, gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
    { id: 'info', label: 'Info', icon: Info, gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' },
];

const STATUS_FILTERS = [
    { id: 'all', label: 'All Status', color: '#1b84ff', bg: '#e8f2ff' },
    { id: 'unread', label: 'Unread', icon: Eye, color: '#ef4444', bg: '#fee2e2' },
    { id: 'read', label: 'Read', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' },
];

const NotificationManagement: FC = () => {
    const navigate = useNavigate();
    const [notificationData, setNotificationData] = useState<INotificationItem[]>([]);

    // State
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState<INotificationItem | null>(null);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
    const [deleteType, setDeleteType] = useState<'single' | 'bulk' | 'all'>('single');
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showImportantOnly, setShowImportantOnly] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [hasMore, setHasMore] = useState(false);
    const [starredTotal, setStarredTotal] = useState(0);

    useEffect(() => {
        const debounceTimer = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
            setPage(1);
        }, 2000);

        return () => window.clearTimeout(debounceTimer);
    }, [searchTerm]);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getNotifications({
                offset: (page - 1) * limit,
                limit,
                search: debouncedSearchTerm || undefined,
                notification_type: filterType === 'all' ? undefined : filterType,
                only_unread: filterStatus === 'all' ? undefined : filterStatus === 'read' ? 0 : 1,
                is_starred: showImportantOnly ? 1 : undefined,
            });
            if (response.status === 'ok') {
                const list = response.data?.list || [];
                setNotificationData(list);
                setHasMore(response.data?.has_more === true);
                setStarredTotal(response.data?.is_starred_count || 0);
                setSelectedIds([]);
            } else console.error('Unable to load notifications:', response.message);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm, filterStatus, filterType, limit, page, showImportantOnly]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Memoized handlers
    const handleBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/dashboard");
        }
    }, [navigate]);

    const handleSetSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setPage(1);
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearchTerm('');
        setPage(1);
    }, []);

    const handleFilterType = useCallback((type: string) => {
        setFilterType(type);
        setPage(1);
        setLimit(10);
    }, []);

    const handleFilterStatus = useCallback((status: string) => {
        setFilterStatus(status);
        setPage(1);
        setLimit(10);
    }, []);

    const handleToggleImportantOnly = useCallback(() => {
        setShowImportantOnly(prev => !prev);
        setPage(1);
        setLimit(10);
    }, []);

    const handleToggleSelect = useCallback((id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    }, []);

    const handleToggleSelectAll = useCallback(() => {
        setSelectedIds(prev =>
            prev.length === notificationData.length ? [] : notificationData.map(n => n.id)
        );
    }, [notificationData]);

    const handleCloseDetailModal = useCallback(() => {
        setShowDetailModal(false);
        setSelectedNotification(null);
    }, []);

    const handleShowDetailModal = useCallback((notification: INotificationItem) => {
        setSelectedNotification({ ...notification, is_read: true });
        setShowDetailModal(true);
        !notification.is_read && handleSingleNotificationAction(notification.id, { is_read: true });
    }, []);

    // Memoized functions
    const getTypeConfig = useCallback((type: string) =>
        TYPE_CONFIG[type?.toLowerCase() as keyof typeof TYPE_CONFIG] || TYPE_CONFIG.info, []);

    const formatDateTime = useCallback((dateString: string) => {
        if (!dateString) return { date: 'Unknown', time: '' };
        const date = new Date(dateString);
        const now = new Date();
        const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        let dateStr;
        if (diffMins < 1) dateStr = 'Just now';
        else if (diffMins < 60) dateStr = `${diffMins} min ago`;
        else if (diffHours < 24) dateStr = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        else if (diffDays < 7) dateStr = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        else dateStr = CustomDateFormate(date);

        return {
            date: dateStr,
            time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        };
    }, []);

    const handleApiCall = useCallback(async (apiFn: Function, payload: any, onSuccess: () => void) => {
        setLoading(true);
        try {
            const response = await apiFn(payload) as ApiResponse;
            if (response.status === "ok") onSuccess();
            else console.error("Operation failed:", response.message);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateNotifications = useCallback(async (ids: number[], updates: Partial<INotificationItem>) => {
        if (!ids.length) return;
        await handleApiCall(updateNotificationFlags, { ids, ...updates }, () => {
            ids.forEach(id => {
                const notification = notificationData.find(n => n.id === id);
                if (notification) {
                    setNotificationData(previous => previous.map(item =>
                        item.id === id ? { ...item, ...updates } : item
                    ));
                }
            });
        });
    }, [notificationData, handleApiCall]);

    const deleteNotifications = useCallback(async (ids: number[]) => {
        if (!ids.length) return;
        await handleApiCall(deleteNotificationFlags, { ids }, () => {
            setNotificationData(previous => previous.filter(notification => !ids.includes(notification.id)));
            setSelectedIds([]);
            setPendingDeleteIds([]);
        });
    }, [handleApiCall]);

    const confirmDelete = useCallback((ids: number[], type: 'single' | 'bulk' | 'all') => {
        setPendingDeleteIds(ids);
        setDeleteType(type);
        setShowDeleteModal(true);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setShowDeleteModal(false);
        setPendingDeleteIds([]);
    }, []);

    const handleConfirmedDelete = useCallback(async () => {
        setShowDeleteModal(false);
        await deleteNotifications(pendingDeleteIds);
    }, [deleteNotifications, pendingDeleteIds]);

    const handleAction = useCallback(async (ids: number[], updates: Partial<INotificationItem>) => {
        await updateNotifications(ids, updates);
        setSelectedIds([]);
    }, [updateNotifications]);

    const handleMarkAllRead = useCallback(() => {
        const unreadIds = notificationData.filter(n => !n.is_read).map(n => n.id);
        if (unreadIds.length) updateNotifications(unreadIds, { is_read: true });
    }, [notificationData, updateNotifications]);

    const handleMarkAllUnread = useCallback(() => {
        const readIds = notificationData.filter(n => n.is_read).map(n => n.id);
        if (readIds.length) updateNotifications(readIds, { is_read: false });
    }, [notificationData, updateNotifications]);

    const handleMarkAllImportant = useCallback(() => {
        const unstarredIds = notificationData.filter(n => !n.is_starred).map(n => n.id);
        if (unstarredIds.length) updateNotifications(unstarredIds, { is_starred: true });
    }, [notificationData, updateNotifications]);

    const handleMarkAllUnimportant = useCallback(() => {
        const starredIds = notificationData.filter(n => n.is_starred).map(n => n.id);
        if (starredIds.length) updateNotifications(starredIds, { is_starred: false });
    }, [notificationData, updateNotifications]);

    const handleDeleteAll = useCallback(() => {
        const allIds = notificationData.map(n => n.id);
        if (allIds.length) confirmDelete(allIds, 'all');
    }, [notificationData, confirmDelete]);

    const handleSingleNotificationAction = useCallback((id: number, updates: Partial<INotificationItem>) => {
        updateNotifications([id], updates);
    }, [updateNotifications]);

    const handleSingleDelete = useCallback((id: number) => {
        confirmDelete([id], 'single');
    }, [confirmDelete]);

    // Memoized computed values
    const bulkActionState = useMemo(() => {
        if (!selectedIds.length) return { isReadAction: true, isImportantAction: true };
        const items = notificationData.filter(n => selectedIds.includes(n.id));
        const allRead = items.every(n => n.is_read);
        const allImportant = items.every(n => n.is_starred);
        return { isReadAction: !allRead, isImportantAction: !allImportant };
    }, [selectedIds, notificationData]);

    const getDeleteModalMessage = useMemo(() => {
        const count = pendingDeleteIds.length;
        switch (deleteType) {
            case 'all': return `Are you sure you want to delete all ${count} Notification Data? This action cannot be undone.`;
            case 'bulk': return `Are you sure you want to delete ${count} selected Notification Data? This action cannot be undone.`;
            default: return `Are you sure you want to delete this notification? This action cannot be undone.`;
        }
    }, [pendingDeleteIds.length, deleteType]);

    const selectedNotificationsList = useMemo(() => {
        if (deleteType !== 'bulk') return [];
        return pendingDeleteIds.slice(0, 5).map(id => notificationData.find(n => n.id === id)).filter(Boolean);
    }, [pendingDeleteIds, deleteType, notificationData]);

    const hasMoreNotifications = useMemo(() =>
        deleteType === 'bulk' && pendingDeleteIds.length > 5,
        [deleteType, pendingDeleteIds.length]);

    const remainingCount = useMemo(() =>
        pendingDeleteIds.length - 5,
        [pendingDeleteIds.length]);

    // Memoized filter chips
    const typeFilterButtons = useMemo(() =>
        TYPE_FILTERS.map(filter => (
            <button key={filter.id} onClick={() => handleFilterType(filter.id)}
                className="btn rounded-pill d-flex align-items-center gap-2 px-4 py-2"
                style={{
                    background: filterType === filter.id ? filter.gradient : '#f8f9fa',
                    color: filterType === filter.id ? 'white' : '#6c757d',
                }}>
                <filter.icon size={14} />
                <span className="fw-semibold small">{filter.label}</span>
            </button>
        )), [filterType, handleFilterType]);

    const statusFilterButtons = useMemo(() =>
        STATUS_FILTERS.map(filter => (
            <button key={filter.id} onClick={() => handleFilterStatus(filter.id)}
                className="btn rounded-pill d-flex align-items-center gap-2 px-4 py-2"
                style={{
                    background: filterStatus === filter.id ? filter.bg : '#f8f9fa',
                    color: filterStatus === filter.id ? filter.color : '#6c757d',
                    border: filterStatus === filter.id ? `1px solid ${filter.color}` : '1px solid #e9ecef',
                }}>
                {filter.icon && <filter.icon size={12} />}
                <span className="small fw-semibold">{filter.label}</span>
            </button>
        )), [filterStatus, handleFilterStatus]);

    const bulkActionButtons = useMemo(() => {
        if (!selectedIds.length) return null;
        return (
            <div className="d-flex gap-2">
                <Button size="sm" variant={bulkActionState.isReadAction ? "light-success" : "light-"}
                    onClick={() => handleAction(selectedIds, { is_read: bulkActionState.isReadAction })}
                    disabled={loading}>
                    {bulkActionState.isReadAction ? <><Eye size={14} className="me-1" /> Mark Read</> : <><EyeOff size={14} className="me-1" /> Mark Unread</>}
                </Button>
                <Button size="sm" variant={bulkActionState.isImportantAction ? "light-warning" : "light-secondary"}
                    onClick={() => handleAction(selectedIds, { is_starred: bulkActionState.isImportantAction })}
                    disabled={loading}>
                    {bulkActionState.isImportantAction ? <><Star size={14} className="me-1" /> Mark Important</> : <><StarOff size={14} className="me-1" /> Mark Unimportant</>}
                </Button>
                <Button size="sm" variant="light-danger" onClick={() => confirmDelete(selectedIds, 'bulk')} disabled={loading}>
                    <Trash2 size={14} className="me-1" /> Delete
                </Button>
            </div>
        );
    }, [selectedIds.length, bulkActionState, loading, handleAction, confirmDelete]);

    const { allRead, allImportant } = useMemo(() => {
        return {
            allRead: notificationData.every(n => n.is_read === true),
            allImportant: notificationData.every(n => n.is_starred === true),
        };
    }, [notificationData]);

    const allActionButtons = useMemo(() => {
        const buttons = [];

        // Read / Unread toggle
        if (allRead) {
            buttons.push({
                icon: EyeOff,
                label: "Mark All Unread",
                action: handleMarkAllUnread
            });
        } else {
            buttons.push({
                icon: Eye,
                label: "Mark All Read",
                action: handleMarkAllRead
            });
        }

        // Important / Unimportant toggle
        if (allImportant) {
            buttons.push({
                icon: StarOff,
                label: "Mark All Unimportant",
                action: handleMarkAllUnimportant
            });
        } else {
            buttons.push({
                icon: Star,
                label: "Mark All Important",
                action: handleMarkAllImportant
            });
        }

        // Always show delete
        buttons.push({
            icon: Trash2,
            label: "Delete All",
            action: handleDeleteAll
        });

        return buttons;
    }, [
        notificationData,
        handleMarkAllRead,
        handleMarkAllUnread,
        handleMarkAllImportant,
        handleMarkAllUnimportant,
        handleDeleteAll
    ]);

    // Memoized notification items
    const notificationItems = useMemo(() =>
        notificationData.map(notification => {
            const config = getTypeConfig(notification.notification_type);
            const Icon = config.icon;
            const isUnread = !notification.is_read;
            const isImportant = notification.is_starred;
            const dateTime = formatDateTime(notification.creation_date);

            return (
                <div key={notification.id} className="card border-0 shadow-sm rounded-4 mb-3 hover-lift">
                    <div className={`list-group-item p-3 rounded-4 ${isUnread ? 'bg-light' : 'bg-white'}`}
                        style={{ cursor: 'pointer', borderLeft: `3px solid ${config.border}`, background: isImportant ? '#fefce8' : (isUnread ? config.bg : 'white') }}
                        onClick={() => handleShowDetailModal(notification)}>
                        <div className="d-flex align-items-start gap-3">
                            <Form.Check type="checkbox" checked={selectedIds.includes(notification.id)}
                                onChange={() => handleToggleSelect(notification.id)}
                                onClick={e => e.stopPropagation()} className="mt-4" disabled={loading} />
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 44, height: 44, background: config.gradient }}>
                                <Icon size={22} className="text-white" />
                            </div>
                            <div className="flex-grow-1">
                                <div className="d-flex justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fw-bold">{notification.title} {notification.extra?.projectShortName && `- ${notification.extra.projectShortName}`}</span>
                                        {/* {notification.priority && PRIORITY_CONFIG[notification.priority as keyof typeof PRIORITY_CONFIG] && (
                                            <Badge bg="light" className="text-dark">
                                                {PRIORITY_CONFIG[notification.priority as keyof typeof PRIORITY_CONFIG].icon} {PRIORITY_CONFIG[notification.priority as keyof typeof PRIORITY_CONFIG].label}
                                            </Badge>
                                        )} */}
                                        {notification.is_important && <span className='bg-danger text-white rounded-circle px-2 fw-bold'>!</span>}
                                    </div>
                                    <div className="d-flex gap-3 text-muted small">
                                        <span><Calendar size={11} /> {dateTime.date}</span>
                                        <span><Clock size={11} /> {dateTime.time}</span>
                                        <span><Award size={11} /> {(notification.type === "evaluation_reminder" || notification.type === "evaluation_completed") && "Evaluation"}</span>
                                        {(notification.type === "evaluation_reminder" || notification.type === "evaluation_completed")
                                            && notification.extra?.evaluationScore !== undefined
                                            && notification.extra?.evaluationScore !== null
                                            && notification.extra?.evaluationScore !== ''
                                            && (
                                                <span>Score: {notification.extra.evaluationScore}</span>
                                            )}
                                    </div>
                                </div>
                                <p className="text-muted small mb-2">{notification.message?.length > 100 ? notification.message.substring(0, 100) + '...' : notification.message}</p>
                                <div className="d-flex gap-3 flex-wrap">
                                    <OverlayTrigger
                                        placement="auto"
                                        overlay={<Tooltip>{notification.is_read ? 'Mark Unread' : 'Mark Read'}</Tooltip>}
                                    >
                                        <Button variant="link" size="sm" className="p-0 text-success"
                                            onClick={(e) => { e.stopPropagation(); handleSingleNotificationAction(notification.id, { is_read: !notification.is_read }); }}>
                                            {notification.is_read ? <EyeOff size={14} /> : <Eye size={14} />}
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                        placement="auto"
                                        overlay={<Tooltip>{isImportant ? 'Remove from important' : 'Mark as important'}</Tooltip>}
                                    >
                                        <Button variant="link" size="sm" className="p-0 text-warning"
                                            onClick={(e) => { e.stopPropagation(); handleSingleNotificationAction(notification.id, { is_starred: !isImportant }); }}>
                                            {isImportant ? <StarOff size={14} /> : <Star size={14} />}
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                        placement="auto"
                                        overlay={<Tooltip>Delete</Tooltip>}
                                    >
                                        <Button variant="link" size="sm" className="p-0 text-danger"
                                            onClick={(e) => { e.stopPropagation(); handleSingleDelete(notification.id); }}>
                                            <Trash2 size={14} />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }), [notificationData, selectedIds, loading, getTypeConfig, formatDateTime, handleShowDetailModal, handleToggleSelect, handleSingleNotificationAction, handleSingleDelete]);

    const isFiltered = filterStatus !== 'all' || filterType !== 'all' || showImportantOnly;

    return (
        <Content>
            <div className="card border-0 shadow-lg rounded-4 overflow-hidden my-8">
                <div className="p-3 p-md-4 border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <div className="bg-primary bg-opacity-10 p-3 rounded-3">
                                <BellIcon size={24} className="text-primary" />
                            </div>
                            <div>
                                <h3 className="fw-bold text-dark mb-0">Notification Management</h3>
                                <p className="text-muted mb-0">Manage and organize all your Notification Data</p>
                            </div>
                        </div>
                        <button className="btn btn-sm btn-light" onClick={handleBack}>
                            <ChevronLeft size={16} className="me-1" /> Back
                        </button>
                    </div>
                </div>

                <div className="card-body p-4 bg-light">
                    {/* Filters */}
                    <div className="card border-0 rounded-4 shadow-sm mb-4">
                        <div className="card-body p-0 bg-secondary">
                            <div className="d-flex flex-wrap gap-4 align-items-center justify-content-between">
                                <div className="d-flex flex-wrap align-items-center gap-4">
                                    <div className='card-body bg-white bg-opacity-20 shadow-sm p-3 rounded-pill' >
                                        <div className="d-flex gap-2 flex-wrap">
                                            {typeFilterButtons}
                                        </div>
                                    </div>
                                    <div className='card-body bg-white bg-opacity-20 shadow-sm p-3 rounded-pill' >
                                        <div className="d-flex gap-2">
                                            {statusFilterButtons}
                                        </div>
                                    </div>
                                    <div className=''>
                                        <button onClick={handleToggleImportantOnly}
                                            className="btn rounded-pill d-flex align-items-center gap-2 px-5 py-3"
                                            style={{
                                                background: showImportantOnly ? '#fef3c7' : '#f8f9fa',
                                                color: showImportantOnly ? '#d97706' : '#6c757d',
                                                border: showImportantOnly ? '1px solid #f59e0b' : '1px solid #e9ecef',
                                            }}>
                                            <Star size={14} fill={showImportantOnly ? '#f59e0b' : 'none'} />
                                            <span className="small fw-semibold">Important ({starredTotal})</span>
                                        </button>
                                    </div>
                                    {isFiltered && <div className=''>
                                        <button onClick={() => { setFilterType('all'); setFilterStatus('all'); setShowImportantOnly(false); setPage(1); }}
                                            className="btn rounded-pill d-flex align-items-center gap-2 px-4 py-3"
                                            style={{
                                                background: '#f8f9fa',
                                                color: '#1b84ff',
                                                border: '1px solid #1b84ff',
                                            }}>
                                            <XCircle size={14} />
                                            <span className="small fw-semibold">Clear All</span>
                                        </button>
                                    </div>}
                                </div>
                                <div className="w-100 w-xl-300px">
                                    <InputGroup>
                                        <InputGroup.Text><Search size={14} /></InputGroup.Text>
                                        <Form.Control type="text" placeholder="Search..." value={searchTerm}
                                            onChange={handleSetSearchTerm} className="bg-light" />
                                        {searchTerm && <Button variant="light" onClick={handleClearSearch}>Clear</Button>}
                                    </InputGroup>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="card border-0 rounded-4 shadow-sm mb-4">
                        <div className="card-body p-4">
                            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-3">
                                    <Form.Check type="checkbox"
                                        checked={selectedIds.length === notificationData.length && notificationData.length > 0}
                                        onChange={handleToggleSelectAll}
                                        label={`Select All (${notificationData.length})`}
                                        disabled={loading} />
                                    {bulkActionButtons}
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    {allActionButtons.map((btn, idx) => (
                                        <OverlayTrigger
                                            key={idx}
                                            placement="auto"
                                            overlay={<Tooltip id={`tooltip-${idx}`}>{btn.label}</Tooltip>}
                                        >
                                            <Button key={idx} size="sm" variant="light" onClick={btn.action} disabled={loading}>
                                                <btn.icon size={14} className="me-1" />
                                            </Button>
                                        </OverlayTrigger>
                                    ))}
                                </div>
                            </div>
                            {loading && <div className="text-center mt-3"><div className="spinner-border spinner-border-sm text-primary me-2" />Updating...</div>}
                        </div>
                    </div>

                    {/* Notifications List */}
                    {notificationItems}

                    {!notificationData.length && (
                        <div className="text-center py-5">
                            <Bell size={64} className="text-muted opacity-25 mb-3" />
                            <h5 className="text-muted">No Notification Data found</h5>
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4">
                        <div className="d-flex align-items-center gap-2 text-muted small">
                            <span>Rows per page</span>
                            <Form.Select
                                size="sm"
                                value={limit}
                                onChange={event => { setLimit(Number(event.target.value)); setPage(1); }}
                                style={{ width: '80px' }}
                                disabled={loading}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </Form.Select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button size="sm" variant="light" disabled={loading || page === 1} onClick={() => setPage(current => current - 1)}>
                                Previous
                            </Button>
                            <span className="small text-muted">Page {page}</span>
                            <Button size="sm" variant="light" disabled={loading || !hasMore} onClick={() => setPage(current => current + 1)}>
                                Next
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <div className="d-flex align-items-center gap-2">
                        <div className="rounded-circle bg-danger bg-opacity-10 p-2">
                            <AlertIcon size={24} className="text-danger" />
                        </div>
                        <Modal.Title className="fw-bold">Confirm Delete</Modal.Title>
                    </div>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    <p className="mb-0">{getDeleteModalMessage}</p>
                    {deleteType === 'bulk' && selectedNotificationsList.length > 0 && (
                        <div className="mt-3 p-3 bg-light rounded-3">
                            <small className="text-muted d-block mb-2">Selected notificationData:</small>
                            <div className="d-flex flex-wrap gap-1">
                                {selectedNotificationsList.map(notif => notif && (
                                    <Badge key={notif.id} bg="light" className="text-dark me-1 mb-1">
                                        {notif.title?.length > 30 ? notif.title.substring(0, 30) + '...' : notif.title}
                                    </Badge>
                                ))}
                                {hasMoreNotifications && (
                                    <Badge bg="light" className="text-dark">
                                        +{remainingCount} more
                                    </Badge>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={handleCloseDeleteModal} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmedDelete} disabled={loading}>
                        {loading ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 size={14} className="me-1" />
                                Delete {deleteType === 'all' ? 'All' : pendingDeleteIds.length} {pendingDeleteIds.length === 1 ? 'Notification' : 'Notifications'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Notification Detail Modal */}
            <NotificationDetailModal
                show={showDetailModal}
                onHide={handleCloseDetailModal}
                notification={selectedNotification}
                setSelectedNotification={setSelectedNotification}
            />

            <style>{`.hover-lift { transition: transform 0.2s ease; } .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.1) !important; }`}</style>
        </Content>
    );
};

export default NotificationManagement;